import { DateRangeProps, StringMap } from "../../components";
import { toISODateFromDMY } from "../../utils";

// "Estado tarjetas": columnas de docs/Estado Tarjetas.xlsx salvo KBInfo (ya no
// se usa). Solo tarjetas vendidas. Estado, saldo y fechas de última
// activación/consumo los calcula el back a partir de las operaciones de la tarjeta.
export interface ReportStatusCardDataProps {
  id: number;
  status: ReportStatusType;
  brandName: string | null;
  expirationDate: string | null;
  activationCode: string | null; // campo "card" del MDW
  lastActivation: string | null;
  lastRedemption: string | null;
  availableBalance: number;
  amount: number; // SALDO INICIAL: importe de la activación
  redeemed: number;
  ean: string | null;
  retailer: string | null; // canal que la vendió (solo para filtrar)
}

export type ReportStatusType =
  | "active"
  | "completed"
  | "cancelled"
  | "refunded"
  | "expired";

// Informes de ventas de marca y canal (columnas acordadas con el cliente, ver
// ReportSalesTable).
export interface ReportSalesProps {
  id: number;
  idProd: string | null; // "Código Prod" del MDW
  productName: string | null;
  date: string;
  fiTerminal: string | null;
  retailer: string | null;
  establishment: string | null;
  operation: ReportOperationType;
  // Solo en activaciones: operación que la anuló o devolvió después.
  reversedBy: Exclude<ReportOperationType, "activation"> | null;
  operationLabel: string | null; // texto de origen; no se muestra
  operationResult: string;
  cardNumber: string | null; // ya enmascarada por el back
  value: number | null;
  sign: number | null;
  amount: number | null;
  ean: string | null;
  media: string | null;
  brandName: string | null;
}

// Ordena filas de Informes por un campo del dato: números como números, fechas
// ISO y textos como texto; los vacíos siempre al final.
export const sortReportRows = <T,>(
  rows: T[],
  key: keyof T,
  direction: "asc" | "desc"
): T[] =>
  [...rows].sort((a, b) => {
    const aVal = a[key] as unknown;
    const bVal = b[key] as unknown;
    const aEmpty = aVal === null || aVal === undefined || aVal === "";
    const bEmpty = bVal === null || bVal === undefined || bVal === "";
    if (aEmpty || bEmpty) return aEmpty === bEmpty ? 0 : aEmpty ? 1 : -1;

    const result =
      typeof aVal === "number" && typeof bVal === "number"
        ? aVal - bVal
        : String(aVal).localeCompare(String(bVal), "es", { numeric: true });
    return direction === "asc" ? result : -result;
  });

export interface ReportOwnSalesDataProps extends ReportSalesProps {}

export interface ReportSalesMyProductsDataProps extends ReportSalesProps {}

// Los informes de ventas solo contienen activaciones, devoluciones y
// cancelaciones; las redenciones (consumo de saldo) cuentan para "Estado tarjetas".
export type ReportOperationType = "activation" | "refund" | "cancellation";

// Opciones de los desplegables de filtro de los informes de ventas
// (GET /ownsales/filter-options y /transactions/filter-options): sobre todas las
// ventas, no solo la página mostrada. Todos los filtros se aplican en el servidor.
export interface ReportSalesFilterOptionsProps {
  products: { idProd: string; productName: string | null }[];
  categories: string[];
  retailer: string[];
  fiTerminal: string[];
  establishment: string[];
}

export const EMPTY_SALES_FILTER_OPTIONS: ReportSalesFilterOptionsProps = {
  products: [],
  categories: [],
  retailer: [],
  fiTerminal: [],
  establishment: [],
};

// Una página del informe de ventas (paginado en el servidor).
export interface ReportSalesPageProps {
  items: ReportSalesProps[];
  total: number;
  page: number;
  pageSize: number;
}

// Ids de filtro de los informes de ventas.
export const SALES_CATEGORY_FILTER = "category";
export const OWN_SALES_PRODUCT_FILTER = "productId";
// Desplegable de FiTerminal (igualdad exacta); "fiTerminal" es el campo de texto (contiene).
export const SALES_FI_TERMINAL_SELECT_FILTER = "fiTerminalSelect";

// Parámetros de la consulta de los informes de ventas a partir de los filtros, el
// rango de fechas y la búsqueda. Los usan la tabla y la exportación, para que el
// Excel tenga exactamente lo mismo que el listado.
export const salesQueryParams = (
  filters: StringMap,
  dateRange: DateRangeProps,
  search: string
): Record<string, string | undefined> => ({
  operation: filters.operation,
  idProd: filters[OWN_SALES_PRODUCT_FILTER],
  category: filters[SALES_CATEGORY_FILTER],
  fiTerminal: filters[SALES_FI_TERMINAL_SELECT_FILTER],
  fiTerminalContains: filters.fiTerminal,
  retailer: filters.retailer,
  establishment: filters.establishment,
  search: search.trim() || undefined,
  dateFrom: toISODateFromDMY(dateRange.startDate),
  dateTo: toISODateFromDMY(dateRange.endDate),
});

export const productOptionLabel = (product: {
  idProd: string;
  productName: string | null;
}): string =>
  product.productName ? `${product.productName} (${product.idProd})` : product.idProd;

export enum ReportsTabEnum {
  ReportsStatusCard = "status-card",
  ReportsOwnSales = "own-sales",
  ReportsSalesMyProducts = "sales-my-products",
}

// Fila de cualquier pestaña de Informes.
export type ReportTabRow = ReportSalesProps | ReportStatusCardDataProps;

export interface ReportTabsBaseProps {
  search: string;
  filters: StringMap;
  dateRange: DateRangeProps;
  setExportDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  setErrorFetchData: React.Dispatch<React.SetStateAction<boolean>>;
  // Filas cargadas (sin filtrar en el cliente): de ellas salen las opciones de los
  // desplegables de filtro de retailer, FiTerminal, punto de venta y marca.
  onRowsChange?: (rows: ReportTabRow[]) => void;
}

export interface ReportStatusCardsProps extends ReportTabsBaseProps {}

export interface ReportOwnSalesProps extends ReportTabsBaseProps {}

export interface ReportSalesMyProductsProps extends ReportTabsBaseProps {}
